import { Suspense } from "react";
import moment from "moment/moment";
import { Provider } from "react-redux";
import { toast, Slide } from "react-toastify";
import BSpinner from "react-bootstrap/Spinner";
import { render } from "@testing-library/react";
import {
  RouterProvider,
  createBrowserRouter,
  createMemoryRouter,
} from "react-router";

import ErrorBoundary from "./components/errorBoundary/errorBoundary";
import { SignalRProvider } from "../system/signalRContext";

import {
  COLORS,
  LoginRoles,
  emptyString,
  KEYBOARD_KEYS,
  errorMessages,
  loginConstants,
  columnNamePrevious,
  PASSWORD_MIN_LENGTH,
  performanceScreensConstants,
  AI_REPLY_THREAD_ERROR_PATTERNS,
  reactSelectStylingConst,
} from "../constants";
import {
  franchiseOptionLabel,
  roleTypeEnums,
} from "../views/shared-screens/constants";
import { Spinner } from "./index";
import store from "../system/store";
import { getItemFromStorage } from "../system/storage";
import App from "../App";
import { ENV } from "../config/env";

const { ADD } = franchiseOptionLabel;
const { GUEST_SENTIMENTS } = performanceScreensConstants;

const STRING_TYPE = "string";
const ESCAPED_SINGLE_QUOTE = "''";

export { buildFullUrls } from "./utils/buildUrl";
export { toTestIdSlug, buildTestId } from "./utils/generateTestId";
export { handleAsyncAction } from "./utils/handleAsyncAction";

export const updateObject = (oldObject, updatedProperties) => ({
  ...oldObject,
  ...updatedProperties,
});
//Toast component to show user response messages
export const customToast = (
  AllErrorMessages,
  type,
  time = 2000,
  position = "top-right"
) => {
  const options = {
    autoClose: time,
    type,
    hideProgressBar: true,
    position,
    pauseOnHover: true,
    transition: Slide,
    theme: "colored",
    className: "color",
  };
  const message = AllErrorMessages;
  toast(message, options);
};

//Paginated body for adding filters query
export const paginatedPostRequestQueryBody = (
  pagination,
  pageNumber,
  searchString,
  sortBy,
  filterQuery,
  size
) => {
  //Use the pagenum from params or get it from redux pagination state
  return {
    pageNumber: pageNumber >= 0 ? pageNumber : pagination.number + 1,
    length: size || pagination.size,
    search: escapeSearchQuery(searchString),
    orderBy: `${sortBy.id} ${sortBy.desc ? "desc" : "asc"}`.trim(),
    filterQuery: filterQuery,
  };
};

//Default pagination body exported for other files
export const paginatedPostRequestBody = (
  pagination,
  pageNumber,
  searchString,
  sortBy,
  filterQuery
) => {
  const sortOrder = sortBy?.desc ? "desc" : "asc";
  //Use the pagenum from params or get it from redux pagination state
  return {
    pageNumber: pageNumber >= 0 ? pageNumber : pagination.number + 1,
    length: pagination.size,
    search: searchString,
    orderBy: sortBy ? `${sortBy.id} ${sortOrder}`.trim() : "",
    filterQuery: filterQuery,
  };
};

//Paginated body for marketing Sales page
export const paginatedPostRequestBodyForMarketingSales = ({
  brandId,
  storeId,
  groupId,
  pagination,
  pageNumber,
  fromTime,
  toTime,
  sortBy,
  size,
  search,
}) => {
  //Use the pagenum from params or get it from redux pagination state
  return {
    brandId: brandId || 0,
    storeId: storeId || null,
    groupId: groupId || null,
    pageNumber: pageNumber >= 0 ? pageNumber : pagination.number + 1,
    fromTime: fromTime || new Date(),
    toTime: toTime || new Date(),
    pageSize: size || 10,
    length: size,
    orderBy: `${sortBy.id} ${sortBy.desc ? "desc" : "asc"}`.trim(),
    search: search,
  };
};

//Paginated body for campaign Metrics page
export const paginatedPostRequestBodyForCampaignMetrics = (
  restaurantId,
  pagination,
  pageNumber,
  searchString,
  fromTime,
  toTime
) => {
  //Use the pagenum from params or get it from redux pagination state
  return {
    restaurantId: restaurantId || 0,
    pageNumber: pageNumber >= 0 ? pageNumber : pagination.number + 1,
    length: pagination.size,
    search: searchString,
    fromTime: fromTime || new Date(),
    toTime: toTime || new Date(),
    pageSize: 5,
  };
};
//Default pagination body exported for brand performance and group performance
export const guestSentimentPaginatedPostRequestBody = ({
  pagination,
  pageNumber,
  sortBy,
  filterQuery,
  averageSentiments,
  isFrRoute,
  size,
  isGuestSentimentFilter,
}) => {
  //Use the pagenum from params or get it from redux pagination state
  return {
    pageNumber: pageNumber >= 0 ? pageNumber : pagination.number + 1,
    length: size,
    orderBy: `${sortBy.id} ${sortBy.desc ? "desc" : "asc"}`.trim(),
    filter: filterQuery,
    minAverageGuestRatings: averageSentiments.min,
    maxAverageGuestRatings: averageSentiments.max,
    isFrRoute: isFrRoute,
    isGuestSentimentFilter: isGuestSentimentFilter,
  };
};

//Default pagination body exported for brand participation
export const participationPaginatedPostRequestBody = (
  pagination,
  pageNumber,
  searchString,
  sortBy,
  filterQuery,
  averageParticipation,
  size
) => {
  //Use the pagenum from params or get it from redux pagination state
  return {
    pageNumber: pageNumber >= 0 ? pageNumber : pagination.number + 1,
    length: size,
    search: escapeSearchQuery(searchString),
    orderBy: `${sortBy.id} ${sortBy.desc ? "desc" : "asc"}`.trim(),
    filter: filterQuery,
    minAverageParticipation: averageParticipation.min,
    maxAverageParticipation: averageParticipation.max,
  };
};

//Validate email address
export const isEmailAddressValid = (email) => {
  //Regular expression for email address
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email);
};

export const calculateChangeColumnValue = (userResponsesData) => {
  userResponsesData.forEach((element) => {
    if (element.previousGuestRatings) {
      const value =
        element.averageGuestRatings.toFixed(2) -
        element.previousGuestRatings.toFixed(2);
      if (value > 0) {
        element.change = value.toFixed(2);
        // to identitfy color
        element.isValueGreaterThanZero = true;
      } else {
        element.change = value.toFixed(2);
        element.isValueGreaterThanZero = false;
      }
    } else {
      element.change = 0;
    }
  });
};

export const calculateChangeColumnValueForTransactionRating = (
  userResponsesData
) => {
  userResponsesData.forEach((element) => {
    if (element.previousTransactionRating) {
      const value =
        element.transactionRating.toFixed(2) -
        element.previousTransactionRating.toFixed(2);
      if (value > 0) {
        element.transactionChange = value.toFixed(2);
        // to identitfy color
        element.isTransactionChangeGreaterThanZero = true;
      } else if (value < 0) {
        element.transactionChange = value.toFixed(2);
        element.isTransactionChangeGreaterThanZero = false;
      } else {
        element.transactionChange = value.toFixed(2);
        element.isTransactionChangeEqualToZero = true;
      }
    } else {
      element.transactionChange = 0;
    }
  });
};

export const calculateChangeColumnValueForSentimentRating = (
  userResponsesData
) => {
  userResponsesData.forEach((element) => {
    if (element.previousSentimentRating) {
      const value =
        element.sentimentRating.toFixed(2) -
        element.previousSentimentRating.toFixed(2);
      if (value > 0) {
        element.sentimentChange = value.toFixed(2);
        // to identitfy color
        element.isSentimentChangeGreaterThanZero = true;
      } else if (value < 0) {
        element.sentimentChange = value.toFixed(2);
        element.isSentimentChangeGreaterThanZero = false;
      } else {
        element.sentimentChange = value.toFixed(2);
        element.isSentimentChangeEqualToZero = true;
      }
    } else {
      element.sentimentChange = 0;
    }
  });
};

//Function to generate new GUID
export const createNewGuid = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

//Validate if password has atleast 1 capital letter
export const containsUppercase = (str) => {
  return /[A-Z]/.test(str);
};
//Validate if password has atleast has 1 number
export const containsNumbers = (str) => {
  return /\d/.test(str);
};
//Validate if password has atleast 1 special char
export const containsSpecialChars = (str) => {
  const specialChars = /[`!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~]/;
  return specialChars.test(str);
};

/** Build password checklist flags for login / reset / account-setup forms. */
export const getPasswordValidationState = (
  password = emptyString,
  confirmPassword = emptyString,
  { includeEquality = true } = {},
) => {
  const state = {
    checkCapLetter: containsUppercase(password),
    checkNumber: containsNumbers(password),
    checkSpecialChar: containsSpecialChars(password),
    checkLength: password.length >= PASSWORD_MIN_LENGTH,
  };
  if (includeEquality) {
    state.checkEquality = password ? password === confirmPassword : false;
  }
  return state;
};

// Prevents the input of space
export const handleSpace = (e) => {
  if (e.target.value.length === 0 && e.keyCode === 32) {
    e.preventDefault();
  }
};

//Custome style for react select
export const customStyle = {
  control: (styles) => ({
    ...styles,
    borderRadius: "6px",
    minHeight: "2.3rem",
    border: `1px solid ${COLORS.SILVER_GREY}`,
  }),
};

const { scarlet, ashGrey, borderRadius, minHeight, boxShadow } =
  reactSelectStylingConst;

export const getUserFormSelectStyles = (error, value) => ({
  control: (styles) => ({
    ...styles,
    borderRadius,
    minHeight,
    borderColor: error && !value ? scarlet : ashGrey,
    "&:hover": {
      borderColor: error && !value ? scarlet : ashGrey,
    },
    boxShadow,
  }),
});

//Loading indicator for react select

export const loadingIndicator = () => {
  return (
    <BSpinner animation="border" role="status" size="sm">
      <span className="visually-hidden">Loading...</span>
    </BSpinner>
  );
};

export const setProperty = (targetClass, value) => {
  document.documentElement.style.setProperty(targetClass, value);
};

//Return Unix timestamp in seconds for the start of the day (00:00:00)
export const convertFromDateToUnix = (fromDate) => {
  const date = new Date(fromDate);
  date.setHours(0, 0, 0, 0);
  return date.getTime() / 1000;
};

//Return Unix timestamp in seconds for the end of the day (23:59:59)
export const convertToDateToUnix = (toDate) => {
  const date = new Date(toDate);
  date.setHours(23, 59, 59, 0);
  return date.getTime() / 1000;
};

//React select option style
export const getOptionStyle = (styles, { data }) => {
  if (data.id === ADD) {
    return {
      ...styles,
      color: "blue",
      cursor: "pointer",
    };
  }
  return styles;
};

//React select option lable style
export const getOptionLabel = (option) => {
  if (option.id === ADD) {
    return <span style={{ color: "blue" }}>{option.name}</span>;
  }
  return option.name;
};

// Test-specific providers without ErrorBoundary and SignalRProvider for unit tests
export const withTestProviders = (ui) => (
  <Provider store={store}>
    <ErrorBoundary>
      <SignalRProvider>
        <Suspense fallback={<Spinner />}>{ui}</Suspense>
      </SignalRProvider>
    </ErrorBoundary>
  </Provider>
);


const getSharedBrowserRouter = (Component) =>
  createBrowserRouter([{ path: "/*", element: <Component /> }], {
    initialEntries: ["/"],
  });

const getSharedMemoryRouter = (path = "/", Component) =>
  createMemoryRouter(
    [
      {
        path: "/*",
        element: <App />,
      },
    ],
    {
      initialEntries: [path],
    }
  );

export const renderComponentWithSharedRouter = (Component) => {
  const router = getSharedBrowserRouter(Component);
  return render(withTestProviders(<RouterProvider router={router} />));
};

export const renderRouterComponentWithSharedRouter = (Component) => {
  const router = getSharedMemoryRouter("/", Component);
  return render(withTestProviders(<RouterProvider router={router} />));
};

// Rendering the screens directly for unit tests - matching App.js setup
export const renderComponent = (Component) => {
  const router = createBrowserRouter([{ path: "/*", element: <Component /> }]);
  return render(withTestProviders(<RouterProvider router={router} />));
};

export const renderComponentWithProps = (Component, props) => {
  const router = createBrowserRouter([
    { path: "/*", element: <Component {...props} /> },
  ]);
  return render(withTestProviders(<RouterProvider router={router} />));
};

// Rendering screens with authentication for unit tests
export const renderRouterComponent = (Component) => {
  const router = createMemoryRouter([{ path: "/*", element: <Component /> }]);
  return render(withTestProviders(<RouterProvider router={router} />));
};

// Render component with specific path
export const renderWithMemoryRouter = (Component, path) => {
  const router = getSharedMemoryRouter(path, Component);
  return render(withTestProviders(<RouterProvider router={router} />));
};

// Render component with specific path and fallback
export const renderWithMemoryRouterAndFallback = (Component, path) => {
  const router = getSharedMemoryRouter(path, Component);
  return render(withTestProviders(<RouterProvider router={router} />));
};

// Render component with specific path and state
export const renderWithMemoryRouterAndStateName = (Component, path, name) => {
  const router = createMemoryRouter([{ path: "/*", element: <Component /> }], {
    initialEntries: [{ pathname: path, state: { name } }],
  });
  return render(withTestProviders(<RouterProvider router={router} />));
};

export const convertDateToMonthDayYearFormat = (date) =>
  moment(date).format("MM/DD/YYYY");

export const UTCToDateFormatWithMonthName = (date) => {
  return moment.unix(date).format("MMM D, YYYY");
};
export const UTCToDateFormatWithMonthNameAndTime = (date) => {
  return moment.unix(date).format("MMM D, YYYY hh:mm a");
};

export const UTCToDateFormat = (date) => {
  return moment.unix(date).format("MM/DD/YYYY");
};

export const UTCToDateTimeFormat = (date) => {
  return moment.unix(date).format("MM/DD/YYYY HH:mm:ss");
};

export const UTCToDateTimeFormatWithoutSeconds = (date) => {
  return moment.unix(date).format("MM/DD/YYYY hh:mm a");
};

export const UTCToDateTimeFormatWithoutSecondsForInbox = (date) => {
  return moment.unix(date).format("hh:mm a, MM/DD/YYYY");
};

export const UTCToDateTimeFormatWithMonthAndDay = (date) => {
  return moment.unix(date).format("MMM DD");
};

export const timestampToDateTimeFormat = (timestamp) => {
  //Convert timestamp to the desired format
  return moment.unix(timestamp).format("YYYY-MM-DDTHH:mm:ssZ");
};

export const getCurrentDateInUnixTimeStamp = () => {
  return Math.floor(Date.now() / 1000);
};

export const fileName = (componentName, startDate, endDate) => {
  return `${componentName}_${UTCToDateFormat(startDate)}_to_${UTCToDateFormat(
    endDate
  )}`;
};

const percentageSign = (isPercentage) => {
  return isPercentage ? "%" : "";
};

export const calculateChange = (value, previousValue, isPercentage) => {
  if (!previousValue) {
    return "-";
  }

  const change = value - previousValue;

  if (change === 0) {
    return "-";
  } else if (change > 0) {
    return ` ${change.toFixed(2)}${percentageSign(isPercentage)}`;
  } else {
    return `- ${Math.abs(change).toFixed(2)}${percentageSign(isPercentage)}`;
  }
};

export const updatedColumnHeaders = (columnHeaders, dayCounts) => {
  return columnHeaders.map((header) =>
    header.label === columnNamePrevious
      ? {
        ...header,
        label: `${columnNamePrevious} ${dayCounts === 1 ? `${dayCounts} DAY` : `${dayCounts} DAYS`
          }`,
      }
      : header
  );
};

export const datePreferenceUtility = () => {
  const storedDatePreference = getItemFromStorage(
    ENV.DATE_PREFERENCE
  );
  const datePreference = storedDatePreference
    ? JSON.parse(storedDatePreference)
    : null;

  const currentDate = new Date();
  const startDate = new Date(currentDate);
  startDate.setDate(currentDate.getDate() - 6);

  return datePreference
    ? {
      ...datePreference,
      endDate: new Date(datePreference.endDate),
      startDate: new Date(datePreference.startDate),
    }
    : { startDate, endDate: currentDate };
};

export const getClassName = (value, className) => {
  return value ? className : emptyString;
};

export const getConditionalValues = (condition, positiveCase, negativeCase) => {
  return condition ? positiveCase : negativeCase;
};

export const isSuperAdminWithRole = (roleId) => {
  const userRole = getItemFromStorage(loginConstants.ROLE);
  return (
    roleId === roleTypeEnums.SUPER_ADMIN || userRole === LoginRoles.SUPER_ADMIN
  );
};
export const isSuperOrBrandAdmin = () => {
  const userRole = getItemFromStorage(loginConstants.ROLE);
  return (
    userRole === LoginRoles.SUPER_ADMIN || userRole === LoginRoles.BRAND_ADMIN
  );
};

export const isSuperAdmin = () => {
  const userRole = getItemFromStorage(loginConstants.ROLE);
  return userRole === LoginRoles.SUPER_ADMIN;
};

export const isGroupAdmin = () => {
  const userRole = getItemFromStorage(loginConstants.ROLE);
  return userRole === LoginRoles.GROUP;
};
export const isManagerAdmin = () => {
  const userRole = getItemFromStorage(loginConstants.ROLE);
  return userRole === LoginRoles.MANAGER;
};

//To format the currency and add "$" in the beginning
export const dollarFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const compareNumericString = (rowA, rowB, id, desc) => {
  let a = Number.parseFloat(rowA.getValue(id));
  let b = Number.parseFloat(rowB.getValue(id));
  if (Number.isNaN(a)) {
    //Blanks and non-numeric strings to bottom
    a = desc ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  }
  if (Number.isNaN(b)) {
    b = desc ? Number.NEGATIVE_INFINITY : Number.POSITIVE_INFINITY;
  }
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};

export const isInclude = (text, subString) => {
  return text.includes(subString);
};

export const updateStarRatingSelectedOptions = (options, ratingOption) => {
  const updatedSelectedOption = options.map((filterObj) => {
    const sentimentFilter = `${GUEST_SENTIMENTS} ${filterObj.equalsValue}`;
    const transactionFilter = `(${ratingOption} >= ${filterObj.minStar} AND ${ratingOption} <= ${filterObj.maxStar})`;

    let updatedFilter =
      ratingOption === GUEST_SENTIMENTS ? sentimentFilter : transactionFilter;

    return {
      ...filterObj,
      filter: updatedFilter,
    };
  });

  return updatedSelectedOption;
};

export const getSubstring = (url, substr) => {
  const index = url.indexOf(substr);
  if (index !== -1) {
    return url.substring(index + substr.length);
  }
  return null;
};

export const compactNumberFormat = (num) => {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(num);
};

export const onKeyDownHandler = (event, callBackFunction, condition = true) => {
  // Implement your desired behavior here
  if (event.key === KEYBOARD_KEYS.ENTER || event.key === KEYBOARD_KEYS.SPACE) {
    if (condition) {
      callBackFunction();
    }
  }
};

export const escapeSearchQuery = (query) => {
  if (typeof query !== STRING_TYPE) return query;
  return query.replace(/'/g, ESCAPED_SINGLE_QUOTE);
};

export const isManagerOrGroupAdmin = () => {
  const userRole = getItemFromStorage(loginConstants.ROLE);
  return userRole === LoginRoles.MANAGER || userRole === LoginRoles.GROUP;
};

// Returns error message based on backend error content
export const getAIResponseErrorMessage = (error, errorMessage) => {
  const backendMessage = error?.[0] || error?.message || emptyString;

  if (
    AI_REPLY_THREAD_ERROR_PATTERNS.some((pattern) =>
      backendMessage.includes(pattern)
    )
  ) {
    return errorMessage;
  }

  return errorMessages.UNABLE_TO_GENERATE_RESPONSE;
};
